import { Dispatch } from 'redux';
import * as api from 'app/shared/api';
import { CourseIntensityDto, SchoolConfig, SaveCourseIntensityParams, CourseIntensitySchoolOverrideRequest, SchoolOverride } from 'app/shared/model';
import { getAllSchools } from 'app/shared/api/getSchools';

// ─── Action Types ──────────────────────────────────────────────────────────────

export const FETCH_INTENSITY_LIST = 'COURSE_INTENSITY/FETCH_INTENSITY_LIST';
export const SET_INTENSITY_LIST = 'COURSE_INTENSITY/SET_INTENSITY_LIST';

export const FETCH_SCHOOL_CONFIGS = 'COURSE_INTENSITY/FETCH_SCHOOL_CONFIGS';
export const SET_SCHOOL_CONFIGS = 'COURSE_INTENSITY/SET_SCHOOL_CONFIGS';

// ─── Action Creators ───────────────────────────────────────────────────────────

const setIntensityList = (list: CourseIntensityDto[]) => ({
  type: SET_INTENSITY_LIST,
  payload: list,
});

const setSchoolConfigs = (configs: SchoolConfig[]) => ({
  type: SET_SCHOOL_CONFIGS,
  payload: configs,
});

// ─── Thunk Actions ─────────────────────────────────────────────────────────────

export const fetchIntensityList = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_INTENSITY_LIST });
  try {
    const list = await api.getCourseIntensitiesWithOverrides();
    dispatch(setIntensityList(list));
  } catch (error) {
    console.error('Failed to fetch intensities', error);
    dispatch(setIntensityList([]));
  }
};

export const createOrUpdateIntensity =
  (params: SaveCourseIntensityParams) => async (dispatch: Dispatch) => {
    try {
      await api.saveCourseIntensity(params);
      dispatch(fetchIntensityList() as any);
    } catch (error) {
      console.error('Failed to save intensity', error);
      throw error;
    }
  };

export const fetchSchoolConfigs = (intensityId: number) => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_SCHOOL_CONFIGS });
  try {
    const intensities = await api.getCourseIntensitiesWithOverrides();
    const intensity = intensities.find(i => i.id === intensityId);
    
    if (intensity && intensity.schoolOverrides) {
      const configs: SchoolConfig[] = intensity.schoolOverrides.map((override: SchoolOverride) => ({
        id: override.schoolId,
        schoolIds: [override.schoolId],
        schoolName: override.schoolName,
        lessons: override.numberOfLessons,
        hours: override.numberOfHours,
        cores: override.numberOfCores,
        electives: override.numberOfElectives,
        length: override.lessonLength,
        intensityId: intensityId
      }));
      dispatch(setSchoolConfigs(configs));
    } else {
      dispatch(setSchoolConfigs([]));
    }
  } catch (error) {
    console.error('Failed to fetch school configs', error);
    dispatch(setSchoolConfigs([]));
  }
};

export const createOrUpdateSchoolConfig =
  (params: SchoolConfig & { intensityId: number; startingYear?: number }) => async (dispatch: Dispatch) => {
    try {
      const allSchools = await getAllSchools();
      
      const getSchoolId = (schoolIdentifier: number | string): number => {
        if (typeof schoolIdentifier === 'number') {
          return schoolIdentifier;
        }
        
        const school = allSchools.find(s => 
          s.code === String(schoolIdentifier) || 
          s.id === schoolIdentifier ||
          String(s.id) === String(schoolIdentifier)
        );
        if (school && school.id) {
          return typeof school.id === 'number' ? school.id : Number(school.id);
        }
        
        const numeric = Number(schoolIdentifier);
        if (!isNaN(numeric)) {
          return numeric;
        }
        
        throw new Error(`Unable to resolve school ID for: ${schoolIdentifier}`);
      };

      const overrideRequests: CourseIntensitySchoolOverrideRequest[] = params.schoolIds.map(schoolId => ({
        courseIntensityId: params.intensityId,
        startingYear: params.startingYear || new Date().getFullYear(), // Default to current year
        schoolId: getSchoolId(schoolId),
        lessonLengthInMinutes: params.length,
        coreLessonsPerWeek: params.cores,
        electiveLessonsPerWeek: params.electives,
        numberOfLessons: params.lessons,
        numberOfHours: params.hours
      }));

      const result = await api.saveCourseIntensitySchoolOverride(overrideRequests);
      
      if (!result.ok) {
        throw new Error(result.message || 'Failed to save school config');
      }
      
      dispatch(fetchSchoolConfigs(params.intensityId) as any);
    } catch (error) {
      console.error('Failed to save school config', error);
      throw error;
    }
  };
